const stripToText = raw =>
  String(raw || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li)>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n+/g, '\n')
    .trim();

const escapeRegExp = value =>
  String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Label yang sering muncul di catatan checkpoint logistics */
const KNOWN_LABELS = [
  // Vessel / voyage
  'Vessel Name',
  'Nama Kapal',
  'Ship Name',
  'Voyage Number',
  'Voyage No',
  'No Voyage',
  'Voyage',
  'Vessel',
  // Port / site timing
  'Port of Loading',
  'Port of Discharge',
  'Loading Port',
  'Discharge Port',
  'ETD Port',
  'ETA Port',
  'ATD Port',
  'ATA Port',
  'ETD Site',
  'ETA Site',
  'ATD Site',
  'ATA Site',
  'ETD',
  'ETA',
  'ATD',
  'ATA',
  'POL',
  'POD',
  'Origin',
  'Destination',
  'Asal',
  'Tujuan',
  // Container / cargo
  'Container Number',
  'Container No',
  'No Container',
  'Nomor Container',
  'Container',
  'Seal Number',
  'Seal No',
  'No Seal',
  'Nomor Seal',
  'Commodity',
  'Komoditi',
  'Muatan',
  'Cargo',
  // Trucking / driver
  'Nomor Polisi',
  'No Polisi',
  'No. Polisi',
  'Nopol',
  'Driver Name',
  'Nama Driver',
  'Driver',
  'Sopir',
  'Vehicle',
  'Kendaraan',
  'No DO',
  'Nomor DO',
  'DO Number',
  'DO No',
  'No SJ',
  'Surat Jalan',
  // Misc
  'Remarks',
  'Remark',
  'Catatan',
  'Note',
  'Notes',
  'Keterangan',
  'Status',
  'Lokasi',
  'Location',
  'Site',
  'Gate In',
  'Gate Out',
  'Closing Time',
  'Closing Document',
  'Closing Cargo'
].sort((a, b) => b.length - a.length);

const buildPairsFromMatches = (text, matches) => {
  if (matches.length < 2) return null;

  const sorted = [...matches].sort((a, b) => a.keyStart - b.keyStart);
  const deduped = [];

  sorted.forEach(item => {
    const overlap = deduped.some(
      prev =>
        item.keyStart < prev.valueStart &&
        item.keyStart + item.label.length > prev.keyStart
    );
    if (!overlap) deduped.push(item);
  });

  if (deduped.length < 2) return null;

  const pairs = deduped
    .map((item, index) => {
      const valueEnd =
        index + 1 < deduped.length ? deduped[index + 1].keyStart : text.length;
      return {
        label: item.label,
        value: text.slice(item.valueStart, valueEnd).trim()
      };
    })
    .filter(pair => pair.label && pair.value);

  return pairs.length >= 2 ? pairs : null;
};

const parseByKnownLabels = text => {
  const matches = [];

  KNOWN_LABELS.forEach(label => {
    const re = new RegExp(`(?:^|[\\s,;/\\-])(${escapeRegExp(label)})\\s*:`, 'gi');
    let match;
    while ((match = re.exec(text)) !== null) {
      const labelStart = match.index + match[0].indexOf(match[1]);
      matches.push({
        label,
        keyStart: labelStart,
        valueStart: match.index + match[0].length
      });
    }
  });

  return buildPairsFromMatches(text, matches);
};

/**
 * Fallback: label pendek Title Case / UPPER (1–3 kata) sebelum colon.
 * Hindari menelan value ALL CAPS yang panjang.
 */
const parseByGenericLabels = text => {
  const keyRe =
    /(?:^|[\s,;/])(([A-Z][a-z]+|[A-Z]{2,10})(?:\s+(?:[A-Z][a-z]+|[A-Z]{2,10}|Name|Port|Site|No|Number|Date|Time|Document|Cargo|Polisi|Driver|Container|Seal|Voyage)){0,2})\s*:/g;
  const matches = [];
  let match;

  while ((match = keyRe.exec(text)) !== null) {
    const label = match[1].trim();
    const labelStart = match.index + match[0].indexOf(match[1]);
    // Skip keyword yang terlalu mirip nama kapal (banyak kata ALL CAPS panjang)
    const words = label.split(/\s+/);
    const allCapsLong = words.filter(w => /^[A-Z]{4,}$/.test(w)).length;
    if (allCapsLong >= 2) continue;

    matches.push({
      label,
      keyStart: labelStart,
      valueStart: match.index + match[0].length
    });
  }

  return buildPairsFromMatches(text, matches);
};

/**
 * Coba parse catatan bebas jadi pasangan label:value.
 * Contoh: "Vessel Name : FAJAR BAHARI IX ETD Port : 30 September 2026"
 */
export const parseCheckpointNotePairs = raw => {
  const text = stripToText(raw);
  if (!text) return null;

  return parseByKnownLabels(text) || parseByGenericLabels(text);
};

export const hasCheckpointNoteContent = raw => stripToText(raw).length > 0;
